import { baseApi } from './base'
import { Tag } from './base'
import { ChatRoom, CreateChatRoom } from '../models/chat-room.model'
import { getSocket } from './socket'

type PopulatedRef = string | { _id: string }

const toId = (ref: PopulatedRef): string => (typeof ref === 'string' ? ref : ref?._id)

/**
 * GET /chat-rooms/:id populates `messages.author` / `messages.readBy` (accounts), while the chat UI
 * compares them with user ids. Flatten them back to ids so authors and read receipts resolve.
 */
export function normalizeChatRoom(chatRoom: ChatRoom): ChatRoom {
  if (!chatRoom?.messages) return chatRoom
  return {
    ...chatRoom,
    messages: chatRoom.messages.map((message) => ({
      ...message,
      author: toId(message.author as PopulatedRef),
      readBy: (message.readBy ?? []).map((reader) => toId(reader as PopulatedRef)),
    })),
  }
}

export const chatRoomApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getChatRoom: builder.query<ChatRoom, { chatRoomId: string; limit?: number; skip?: number }>({
      query: (params) => {
        if (typeof params === 'string') {
          return {
            url: `/chat-rooms/${params}`,
            method: 'GET',
          }
        }

        const { chatRoomId, limit, skip } = params
        const searchParams = new URLSearchParams()
        if (limit !== undefined) searchParams.append('limit', limit.toString())
        if (skip !== undefined) searchParams.append('skip', skip.toString())

        return {
          url: `/chat-rooms/${chatRoomId}?${searchParams.toString()}`,
          method: 'GET',
        }
      },
      transformResponse: normalizeChatRoom,
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        try {
          await cacheDataLoaded
          const socket = await getSocket()

          const chatRoomId = typeof arg === 'string' ? arg : arg.chatRoomId
          socket.emit('join_chat', [chatRoomId])

          const chatRoomListener = (chatRoom: ChatRoom) => {
            if (chatRoom._id === chatRoomId) {
              updateCachedData(() => {
                return normalizeChatRoom(chatRoom)
              })
            }
          }
          socket.on('chat_room', chatRoomListener)

          const newMessageListener = ({ chatRoomId: msgChatRoomId, message }: { chatRoomId: string; message: ChatRoom['messages'][number] }) => {
            if (msgChatRoomId === chatRoomId) {
              updateCachedData((draft) => {
                if (draft && draft.messages) {
                  if (!draft.messages.some((m) => m._id === message._id)) {
                    draft.messages.push(message)
                  }
                }
                return draft
              })
            }
          }
          socket.on('new_message', newMessageListener)

          const messageReadListener = ({ chatRoomId: msgChatRoomId, messageId, userId }: { chatRoomId: string; messageId: string; userId: string }) => {
            if (msgChatRoomId === chatRoomId) {
              updateCachedData((draft) => {
                if (draft && draft.messages) {
                  const messageIndex = draft.messages.findIndex((msg) => msg._id === messageId)
                  if (messageIndex !== -1) {
                    const message = draft.messages[messageIndex]
                    if (!message.readBy) {
                      message.readBy = []
                    }

                    if (!message.readBy.includes(userId)) {
                      message.readBy.push(userId)
                    }
                  }
                }
                return draft
              })
            }
          }
          socket.on('message_read', messageReadListener)

          const allMessagesReadListener = ({ chatRoomId: msgChatRoomId, userId }: { chatRoomId: string; userId: string }) => {
            if (msgChatRoomId === chatRoomId) {
              updateCachedData((draft) => {
                if (draft && draft.messages) {
                  let updatedCount = 0
                  draft.messages.forEach((message) => {
                    if (!message.readBy) {
                      message.readBy = []
                    }

                    if (!message.readBy.includes(userId)) {
                      message.readBy.push(userId)
                      updatedCount++
                    }
                  })
                }
                return draft
              })
            }
          }
          socket.on('all_messages_read', allMessagesReadListener)

          await cacheEntryRemoved

          socket.emit('leave_private_chat', chatRoomId)

          socket.off('chat_room', chatRoomListener)
          socket.off('new_message', newMessageListener)
          socket.off('message_read', messageReadListener)
          socket.off('all_messages_read', allMessagesReadListener)
        } catch (error) {
          console.error('Socket error:', error)
        }
      },
    }),
    sendGlobalMessage: builder.mutation<ChatRoom, { content: string; userId: string; chatRoomId: string }>({
      query: ({ content, userId, chatRoomId }) => ({
        url: '/chat-rooms/global/messages',
        method: 'POST',
        body: { content, userId, chatRoomId },
      }),
      invalidatesTags: () => [{ type: Tag.CHAT_ROOM, id: 'GLOBAL_HISTORY' }],
    }),
    sendChatMessage: builder.mutation<ChatRoom, { chatRoomId: string; message: string }>({
      query: ({ chatRoomId, message }) => ({
        url: `/chat-rooms/${chatRoomId}/messages`,
        method: 'POST',
        body: { message },
      }),
      invalidatesTags: (_, __, { chatRoomId }) => [{ type: Tag.CHAT_ROOM, id: chatRoomId }],
    }),
    markMessageAsRead: builder.mutation<ChatRoom, { chatRoomId: string; messageId: string }>({
      query: ({ chatRoomId, messageId }) => ({
        url: `/chat-rooms/${chatRoomId}/messages/${messageId}/read`,
        method: 'PUT',
      }),
      invalidatesTags: (_, __, { chatRoomId }) => [{ type: Tag.CHAT_ROOM, id: chatRoomId }],
    }),
    confirmReadingMessages: builder.mutation<ChatRoom, string>({
      query: (chatRoomId) => ({
        url: `/chat-rooms/${chatRoomId}/messages/read`,
        method: 'PUT',
      }),
      invalidatesTags: (_, __, chatRoomId) => [{ type: Tag.CHAT_ROOM, id: chatRoomId }],
    }),
    createChatRoom: builder.mutation<ChatRoom, CreateChatRoom>({
      query: (body) => ({
        url: '/chat-rooms',
        method: 'POST',
        body,
      }),
      invalidatesTags: () => [{ type: Tag.CHAT_ROOM, id: 'LIST' }],
    }),
    getChatRoomsJoinedByUser: builder.query<ChatRoom[], { isGroup: boolean; limit?: number; skip?: number }>({
      query: ({ isGroup, limit, skip }) => {
        const params = new URLSearchParams({ isGroup: isGroup.toString() })
        if (limit !== undefined) params.append('limit', limit.toString())
        if (skip !== undefined) params.append('skip', skip.toString())
        return {
          url: `/chat-rooms?${params.toString()}`,
          method: 'GET',
        }
      },
    }),
    getChatRoomMessages: builder.query<ChatRoom, { chatRoomId: string; limit?: number; skip?: number }>({
      query: ({ chatRoomId, limit, skip }) => {
        const params = new URLSearchParams()
        if (limit !== undefined) params.append('limit', limit.toString())
        if (skip !== undefined) params.append('skip', skip.toString())
        const queryString = params.toString()
        return {
          url: `/chat-rooms/${chatRoomId}${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        }
      },
      transformResponse: normalizeChatRoom,
    }),
  }),
  overrideExisting: false,
})
